<div class="min-h-screen bg-gray-50 py-6 sm:py-8">
  <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

    <!-- Header -->
    <div class="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 class="text-3xl font-bold tracking-tight text-gray-900">Berichte &amp; Statistik</h1>
        <p class="mt-1 text-sm text-gray-600">
          Übersicht über erfasste Mandate, Tatbestände und Systemaktivität im gewählten Zeitraum.
        </p>
      </div>
    </div>

    <!-- Filters -->
    <div class="mb-8 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div class="mb-4">
        <label class="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-500">Zeitraum</label>
        <div class="flex flex-wrap gap-2">
          <button
            *ngFor="let preset of datePresets"
            type="button"
            (click)="selectDatePreset(preset.id)"
            [disabled]="isLoading"
            class="rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            [class.border-blue-600]="datePreset === preset.id"
            [class.bg-blue-600]="datePreset === preset.id"
            [class.text-white]="datePreset === preset.id"
            [class.border-gray-300]="datePreset !== preset.id"
            [class.bg-white]="datePreset !== preset.id"
            [class.text-gray-700]="datePreset !== preset.id"
            [class.hover:bg-gray-50]="datePreset !== preset.id && !isLoading"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>

      <div class="flex flex-wrap items-end gap-4">
        <div *ngIf="isCustomDateRange">
          <label class="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Von</label>
          <input
            type="date"
            [(ngModel)]="fromDate"
            (ngModelChange)="onCustomDateChange()"
            class="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div *ngIf="isCustomDateRange">
          <label class="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Bis</label>
          <input
            type="date"
            [(ngModel)]="toDate"
            (ngModelChange)="onCustomDateChange()"
            class="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div *ngIf="!isCustomDateRange" class="flex items-center gap-2 pb-2">
          <ng-container *ngIf="canNavigatePeriod">
            <button
              type="button"
              (click)="shiftPeriod(-1)"
              [disabled]="isLoading"
              class="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Vorheriger Zeitraum"
            >
              ‹
            </button>
            <span class="min-w-[10rem] text-center text-sm text-gray-700">{{ periodRangeLabel }}</span>
            <button
              type="button"
              (click)="shiftPeriod(1)"
              [disabled]="isLoading || !canShiftPeriodForward"
              class="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Nächster Zeitraum"
            >
              ›
            </button>
          </ng-container>
          <span *ngIf="!canNavigatePeriod" class="text-sm text-gray-600">{{ periodRangeLabel }}</span>
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Land</label>
          <select
            [(ngModel)]="selectedCountry"
            class="min-w-[5.5rem] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Alle</option>
            <option *ngFor="let land of countryOptions" [value]="land.bezeichnung">
              {{ land.bezeichnung }}
            </option>
          </select>
        </div>
        <button
          type="button"
          (click)="fetchReports()"
          [disabled]="isLoading"
          class="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {{ isLoading ? 'Lädt…' : 'Ermitteln' }}
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div *ngIf="isLoading" class="flex items-center justify-center py-24">
      <div class="h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
      <span class="ml-3 text-sm text-gray-600">Daten werden geladen…</span>
    </div>

    <!-- Error -->
    <div *ngIf="hasError && !isLoading" class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      {{ errorMessage }}
    </div>

    <!-- Empty (filtered range) -->
    <div
      *ngIf="!isLoading && !hasError && reports && !hasData"
      class="rounded-lg border border-gray-200 bg-white py-16 text-center shadow-sm mb-8"
    >
      <p class="text-lg font-medium text-gray-900">Keine Daten im gewählten Zeitraum</p>
      <p class="mt-1 text-sm text-gray-500">Passen Sie den Datumsbereich oder den Land-Filter an.</p>
    </div>

    <ng-container *ngIf="!isLoading && !hasError && reports">

      <!-- Charts & period KPIs -->
      <ng-container *ngIf="hasData">

      <!-- KPI cards -->
      <div class="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Mandate gesamt</p>
          <p class="mt-1 text-3xl font-bold text-gray-900">{{ kpis.total | number }}</p>
        </div>
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Anzeigen</p>
          <p class="mt-1 text-3xl font-bold text-blue-600">{{ kpis.anzeigen | number }}</p>
        </div>
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p class="text-xs font-medium uppercase tracking-wide text-gray-500">BOM</p>
          <p class="mt-1 text-3xl font-bold text-amber-600">{{ kpis.bom | number }}</p>
        </div>
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Aktiv / Offen</p>
          <p class="mt-1 text-3xl font-bold text-emerald-600">{{ kpis.aktiv_offen | number }}</p>
        </div>
        <div class="col-span-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm lg:col-span-1">
          <p class="text-xs font-medium uppercase tracking-wide text-gray-500">Im Zeitraum</p>
          <p class="mt-1 text-3xl font-bold text-gray-900">{{ kpis.im_zeitraum | number }}</p>
        </div>
      </div>

      <!-- Charts -->
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">

        <!-- Trend chart - full width -->
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm lg:col-span-2">
          <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 class="text-base font-semibold text-gray-900">{{ trendChartTitle }}</h2>
            <div class="flex gap-2">
              <button
                type="button"
                (click)="setTrendChartType('line')"
                class="rounded-md border px-3 py-1.5 text-sm font-medium transition-colors"
                [class.border-blue-600]="trendChartType === 'line'"
                [class.bg-blue-600]="trendChartType === 'line'"
                [class.text-white]="trendChartType === 'line'"
                [class.border-gray-300]="trendChartType !== 'line'"
                [class.bg-white]="trendChartType !== 'line'"
                [class.text-gray-700]="trendChartType !== 'line'"
              >
                Linien
              </button>
              <button
                type="button"
                (click)="setTrendChartType('bar')"
                class="rounded-md border px-3 py-1.5 text-sm font-medium transition-colors"
                [class.border-blue-600]="trendChartType === 'bar'"
                [class.bg-blue-600]="trendChartType === 'bar'"
                [class.text-white]="trendChartType === 'bar'"
                [class.border-gray-300]="trendChartType !== 'bar'"
                [class.bg-white]="trendChartType !== 'bar'"
                [class.text-gray-700]="trendChartType !== 'bar'"
              >
                Balken
              </button>
            </div>
          </div>
          <div class="h-72">
            <canvas
              baseChart
              [data]="monthlyChartData"
              [type]="trendChartType"
              [options]="trendChartOptions"
            ></canvas>
          </div>
        </div>

        <!-- Top Tatbestände -->
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 class="mb-4 text-base font-semibold text-gray-900">Top Tatbestände</h2>
          <div class="h-72">
            <canvas baseChart [data]="tatbestandeChartData" [type]="'bar'" [options]="barChartOptions"></canvas>
          </div>
        </div>

        <!-- Status -->
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 class="mb-4 text-base font-semibold text-gray-900">Status-Verteilung</h2>
          <div class="h-72">
            <canvas baseChart [data]="statusChartData" [type]="'doughnut'" [options]="doughnutChartOptions"></canvas>
          </div>
        </div>

        <!-- Anzeige vs BOM -->
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 class="mb-4 text-base font-semibold text-gray-900">Anzeige vs. BOM</h2>
          <div class="h-72">
            <canvas baseChart [data]="bomChartData" [type]="'doughnut'" [options]="doughnutChartOptions"></canvas>
          </div>
        </div>

        <!-- Land -->
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 class="mb-4 text-base font-semibold text-gray-900">Mandate nach Land</h2>
          <div class="h-72">
            <canvas baseChart [data]="countryChartData" [type]="'bar'" [options]="verticalBarChartOptions"></canvas>
          </div>
        </div>

        <!-- Dienstnehmer -->
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 class="mb-4 text-base font-semibold text-gray-900">Top Dienstnehmer</h2>
          <div class="h-72">
            <canvas baseChart [data]="dienstnehmerChartData" [type]="'bar'" [options]="barChartOptions"></canvas>
          </div>
        </div>

        <!-- Weekday -->
        <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm lg:col-span-2">
          <h2 class="mb-4 text-base font-semibold text-gray-900">Aktivität nach Wochentag</h2>
          <div class="h-72">
            <canvas baseChart [data]="weekdayChartData" [type]="'bar'" [options]="verticalBarChartOptions"></canvas>
          </div>
        </div>

      </div>
      </ng-container>
    </ng-container>

  </div>
</div>
